import { useState } from "react";
import { render } from "@rakered/email";

import CloseIcon from "@mui/icons-material/Close";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// My Files
import firebase from "../../../firebase";
import SetPasswordEmail from "../emails/SetPasswordEmail";

export default function AccountRecoveryDialog(props) {
    const [email, setEmail] = useState("");
    const [inProgress, setInProgress] = useState(false);

    function handleCancel() {
        props.dismiss();
        setEmail("");
    }

    function handleOK() {
        if (email !== "") {
            setInProgress(true);
            // First create a new user account in firebase auth. Then change the user.uid property in the database to match this new account.

            // By default, when you create a new user, firebase automatically logs in with that user. To avoid this we create a duplicate instance of firebase here

            var updateUser = firebase.functions().httpsCallable("updateUser");

            updateUser({
                uid: props.user.uid,
                user: { email: email },
            })
                .then(() => {
                    // email address has been updated, now send password reset email
                    var generateEmailActionLink = firebase
                        .functions()
                        .httpsCallable("generateEmailActionLink");

                    generateEmailActionLink({
                        email: email,
                    }).then((result) => {
                        let link = result.data.link;
                        sendPasswordEmail(link);

                        // The address lives only on /userprofiles.
                        firebase
                            .database()
                            .ref("userprofiles")
                            .child(props.user.key)
                            .update({ email: email })
                            .catch((error) => {
                                // Auth already has the new address, so the
                                // recovery itself has worked; only the
                                // profile copy is stale.
                                console.error(
                                    "Unable to save the recovered email to " +
                                        "the user's profile",
                                    error,
                                );
                            });

                        props.onClose(true, "", email);
                        setEmail("");
                        setInProgress(false);
                    });
                })
                .catch((error) => {
                    // Getting the Error details.
                    let errorCode = error.code;
                    let errorMessage = error.message;
                    console.log(errorCode + ":" + errorMessage);
                    props.onClose(false, errorMessage, email);
                    setInProgress(false);
                    setEmail("");
                });
        }
    }

    function sendPasswordEmail(link) {
        var sendMail = firebase.functions().httpsCallable("sendMail");

        sendMail({
            dest: email,
            sender: "Nodiant Online <no.reply.nodiant@gmail.com>",
            subject: "Choose your Nodiant Online Password",
            html: render(
                <SetPasswordEmail
                    link={link}
                    message="Your account on the Nodiant Online platform has been recovered."
                />
            ),
        })
            .then(() => {
                console.log("Email Sent");
            })
            .catch((error) => {
                // Getting the Error details.
                console.log("Error");
                console.log(error.code);
                console.log(error.message);
                console.log(error.details);
            });
    }

    const handleKeyboardInput = (e) => {
        setEmail(e.target.value);
    };

    return (
        <Dialog
            onClose={inProgress ? undefined : handleCancel}
            aria-labelledby="account-recovery-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="xs"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <LockResetOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="account-recovery-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Account recovery
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Move this account to a new email address.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close account recovery"
                    onClick={handleCancel}
                    disabled={inProgress}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                    >
                        Use this when the user has forgotten their password and
                        no longer has access to their current email address. A
                        password setup email will be sent to the new address.
                    </Typography>
                    <TextField
                        value={email}
                        onChange={handleKeyboardInput}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && email !== "") {
                                event.preventDefault();
                                handleOK();
                            }
                        }}
                        autoFocus
                        id="recovery-email"
                        label="New email address"
                        type="email"
                        fullWidth
                        disabled={inProgress}
                        sx={{
                            mt: 2,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button
                    onClick={handleCancel}
                    color="inherit"
                    disabled={inProgress}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleOK}
                    variant="contained"
                    disableElevation
                    disabled={inProgress || email === ""}
                >
                    Recover account
                </Button>
            </DialogActions>
            {inProgress && <LinearProgress />}
        </Dialog>
    );
}
